import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import { cn } from "../lib/cn.js";
import { fieldClasses } from "./input.js";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  id?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

/**
 * Dropdown (DESIGN.md: Select). Looks like an Input; the list is a flat popover. Typing
 * jumps to matching options. Radix injects two inline <style> tags that our CSP blocks
 * (scrollbar hiding, scroll lock); the scroll lock is mirrored in styles.css instead, and the
 * list keeps its thin scrollbar.
 */
export function Select({
  value,
  onValueChange,
  options,
  id,
  disabled,
  className,
  "aria-label": ariaLabel,
}: SelectProps) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          fieldClasses,
          "flex h-9 items-center justify-between gap-2 text-left data-[state=open]:border-ring",
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon className="size-4 shrink-0 text-dim-foreground" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-[min(20rem,var(--radix-select-content-available-height))] w-(--radix-select-trigger-width) border border-input bg-popover text-popover-foreground shadow-window data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-100"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="relative flex h-8 pointer-coarse:h-11 cursor-default items-center pr-3 pl-8 text-sm text-secondary-foreground outline-none select-none data-highlighted:bg-muted data-highlighted:text-foreground data-[state=checked]:text-foreground"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2.5 flex items-center">
                  <CheckIcon className="size-3.5 text-brand" aria-hidden />
                </SelectPrimitive.ItemIndicator>
                <span className="truncate">
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
